import { API_BASE_URL } from './constants';
import type { AccountInterface } from 'starknet';

// --- Wallet signer for authenticated requests ---

let _walletAccount: AccountInterface | null = null;

/**
 * Set the wallet account used to sign authenticated API requests.
 * Call this when the wallet connects, clear with null on disconnect.
 */
export function setWalletSigner(account: AccountInterface | null) {
  _walletAccount = account;
}

const AUTH_TYPED_DATA = {
  types: {
    StarkNetDomain: [
      { name: 'name', type: 'felt' },
      { name: 'version', type: 'felt' },
      { name: 'chainId', type: 'felt' },
    ],
    Auth: [
      { name: 'timestamp', type: 'felt' },
    ],
  },
  primaryType: 'Auth' as const,
  domain: {
    name: 'VBDesk',
    version: '1',
    chainId: 'SN_SEPOLIA',
  },
};

/**
 * Generate authentication headers by signing a timestamp with the wallet.
 */
async function getAuthHeaders(): Promise<Record<string, string>> {
  if (!_walletAccount) {
    throw new Error('Wallet not connected — cannot sign request');
  }

  const timestamp = Date.now().toString();
  const messageData = {
    ...AUTH_TYPED_DATA,
    message: { timestamp },
  };

  const signature = await _walletAccount.signMessage(messageData);
  const sigArray = Array.isArray(signature) ? signature : [signature.r.toString(), signature.s.toString()];

  return {
    'x-wallet-address': _walletAccount.address,
    'x-wallet-signature': JSON.stringify(sigArray),
    'x-wallet-timestamp': timestamp,
  };
}

// --- Custom error class for structured API errors ---

export class ApiError extends Error {
  status: number;
  code: string;
  data: Record<string, unknown>;

  constructor(message: string, status: number, code: string, data: Record<string, unknown>) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.data = data;
  }
}

// --- Base request helpers ---

async function request<T>(path: string, options?: RequestInit): Promise<T> {
  const { headers, ...rest } = options ?? {};
  const res = await fetch(`${API_BASE_URL}${path}`, {
    ...rest,
    headers: { 'Content-Type': 'application/json', ...headers },
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new ApiError(
      body.message || `API error ${res.status}`,
      res.status,
      body.status || 'error',
      body,
    );
  }
  return res.json();
}

async function authenticatedRequest<T>(path: string, options?: RequestInit): Promise<T> {
  const authHeaders = await getAuthHeaders();
  return request<T>(path, {
    ...options,
    headers: { ...authHeaders, ...options?.headers },
  });
}

// --- Batch ---

// Normalized batch info used by frontend
export interface BatchInfo {
  id: number;
  status: string; // COMMIT, REVEAL, CLEARING, SETTLED
  commitDeadline: string; // ISO timestamp
  revealDeadline: string; // ISO timestamp
  uniformPrice?: string | null;
  merkleRoot?: string | null;
  baseToken?: string | null;
  quoteToken?: string | null;
  sellAmount?: string | null;
  reservePrice?: string | null;
  minBid?: string | null;
  totalBids?: number;
}

// Normalize different API response formats into a consistent shape
function normalizeBatch(raw: Record<string, unknown>): BatchInfo {
  return {
    id: Number(raw.batch_id ?? raw.id ?? 0),
    status: String(raw.status ?? ''),
    commitDeadline: String(raw.commit_deadline ?? raw.commitDeadline ?? ''),
    revealDeadline: String(raw.reveal_deadline ?? raw.revealDeadline ?? ''),
    uniformPrice: (raw.uniform_price ?? raw.uniformPrice ?? null) as string | null,
    merkleRoot: (raw.merkle_root ?? raw.merkleRoot ?? null) as string | null,
    baseToken: (raw.base_token ?? raw.baseToken ?? null) as string | null,
    quoteToken: (raw.quote_token ?? raw.quoteToken ?? null) as string | null,
    sellAmount: (raw.sell_amount ?? raw.sellAmount ?? null) as string | null,
    reservePrice: (raw.reserve_price ?? raw.reservePrice ?? null) as string | null,
    minBid: (raw.min_bid ?? raw.minBid ?? null) as string | null,
    totalBids: Number(raw.total_bids ?? raw.totalBids ?? 0),
  };
}

export async function getActiveBatches(): Promise<BatchInfo[]> {
  const data = await request<{ batches: Record<string, unknown>[] }>('/batch/active');
  return data.batches.map(normalizeBatch);
}

export async function getBatchStatus(id: string | number): Promise<BatchInfo> {
  const raw = await request<Record<string, unknown>>(`/batch/${id}/status`);
  return normalizeBatch(raw);
}

export function createBatch(params: {
  id: number;
  commitDuration: number;
  revealDuration: number;
  baseToken?: string;
  quoteToken?: string;
  sellAmount?: string;
  reservePrice?: string;
  minBid?: string;
}): Promise<BatchInfo> {
  return authenticatedRequest('/batch/create', {
    method: 'POST',
    body: JSON.stringify(params),
  });
}

// --- Commit (submit order) ---

export interface CommitRequest {
  batchId: number;
  wallet: string;
  side: 'BUY' | 'SELL';
  commitment: string;
  bidAmount: string; // u256 as string — backend calculates 10% deposit
}

export interface CommitResponse {
  orderId: number;
  batchId: number;
  commitment: string;
  depositAmount: string; // 10% locked in Vault
  depositTxHash: string;
}

export function submitCommit(data: CommitRequest): Promise<CommitResponse> {
  // Map frontend camelCase to backend snake_case DTO
  const payload = {
    batch_id: data.batchId,
    commit_hash: data.commitment,
    wallet_pub: data.wallet,
    bid_amount: data.bidAmount,
  };
  return authenticatedRequest('/commit', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

// --- Reveal ---

export interface RevealRequest {
  orderId: number;
  price: string;
  amount: string;
  salt: string;
}

export interface RevealResponse {
  orderId: number;
  revealed: boolean;
}

export function submitReveal(data: RevealRequest): Promise<RevealResponse> {
  return authenticatedRequest('/reveal', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

// --- Fills ---

export interface Fill {
  orderId: number;
  wallet: string;
  side: string;
  price: string;
  amount: string;
  fillAmount: string;
  fillPrice: string;
  leafHash: string;
}

export function getBatchFills(batchId: string | number): Promise<Fill[]> {
  return request(`/batch/${batchId}/fills`);
}

export function getBatchProof(batchId: string | number, wallet: string): Promise<{
  fills: Fill[];
  proof: string[];
  root: string;
}> {
  return request(`/batch/${batchId}/proof/${wallet}`);
}

// --- Receipt ---

export interface Receipt {
  orderId: number;
  wallet: string;
  side: string;
  price: string;
  amount: string;
  fillAmount: string;
  fillPrice: string;
  leafHash: string;
  batchId: number;
  pair: string;
  settlementTxHash: string | null;
  merkleRoot: string;
  proof: string[];
}

export function getReceipt(orderId: string | number): Promise<Receipt> {
  return request(`/receipt/${orderId}`);
}

export function verifyReceipt(orderId: string | number): Promise<{ valid: boolean; details: Record<string, unknown> }> {
  return request(`/receipt/${orderId}/verify`);
}

// --- History ---

export interface HistoryEntry {
  orderId: number;
  wallet: string;
  side: string;
  pair: string;
  price: string;
  amount: string;
  fillAmount: string;
  fillPrice: string;
  batchId: number;
  settledAt: string;
}

export function getWalletHistory(wallet: string, page = 1, limit = 20): Promise<{
  data: HistoryEntry[];
  total: number;
  page: number;
  limit: number;
}> {
  return request(`/history/${wallet}?page=${page}&limit=${limit}`);
}

// --- Notes ---

export interface NoteInfo {
  commitment: string;
  walletAddress: string;
  token: string;
  amount: string;
  salt: string;
  leafIndex: number;
  isSpent: boolean;
  depositTxHash: string | null;
  createdAt: string;
}

export function prepareDeposit(token: string, amount: string): Promise<{
  commitment: string;
  salt: string;
  leafIndex: number;
  depositTxHash: string | null;
  merkleRoot: string;
}> {
  return authenticatedRequest('/notes/deposit', {
    method: 'POST',
    body: JSON.stringify({ token, amount }),
  });
}

export function confirmDeposit(commitment: string, txHash: string): Promise<{
  commitment: string;
  txHash: string;
}> {
  return authenticatedRequest('/notes/confirm-deposit', {
    method: 'POST',
    body: JSON.stringify({ commitment, txHash }),
  });
}

export function spendNote(commitment: string, recipient: string): Promise<{
  nullifier: string;
  spendTxHash: string | null;
}> {
  return authenticatedRequest('/notes/spend', {
    method: 'POST',
    body: JSON.stringify({ commitment, recipient }),
  });
}

export function getUnspentNotes(wallet: string): Promise<NoteInfo[]> {
  return request(`/notes/${wallet}`);
}

export function getNoteProof(commitment: string): Promise<{
  commitment: string;
  leafIndex: number;
  proof: string[];
  root: string;
}> {
  return request(`/notes/${commitment}/proof`);
}

// --- Ephemeral ---

export function createEphemeral(token: string, amount: string): Promise<{
  depositId: string;
  depositAddress: string;
  expiresAt: string;
}> {
  return authenticatedRequest('/ephemeral/create', {
    method: 'POST',
    body: JSON.stringify({ token, amount }),
  });
}

export function getEphemeralStatus(depositId: string): Promise<{
  depositId: string;
  status: string;
  depositAddress: string;
  funded: boolean;
  swept: boolean;
}> {
  return request(`/ephemeral/${depositId}/status`);
}
